// /split-bank-statement — the wedge page. This is the ONE page on the
// internet that can truthfully say "drop in a whole statement and split it",
// because (per COMPETITOR_RESEARCH.md, July 2026 re-verification) no
// competitor does statement-level splitting without bank linking, and none
// have bulk edit. Claim hygiene: every named-competitor claim below traces
// to COMPETITOR_RESEARCH.md — if it isn't there, it doesn't ship.
import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FileText, Camera, CheckSquare, CalendarRange, ShieldCheck, ArrowRight } from 'lucide-react'
import PublicShell from '../components/PublicShell'

const FAQ = [
  {
    q: 'How do I split a whole credit card statement with friends?',
    a: 'Upload the PDF or CSV statement (or a screenshot of your banking app) to AutoSplit. It extracts every transaction, auto-categorizes them, suggests who splits what, and computes the fewest payments to settle up. No typing charges in one by one.',
  },
  {
    q: 'Do I have to connect my bank account?',
    a: 'No. AutoSplit never links to your bank. You upload the statement file or screenshot you already have — you choose exactly what the app sees.',
  },
  {
    q: 'What about charges on the statement that were not part of the trip?',
    a: "Trip mode auto-excludes everyday spending outside your trip dates, flags pre-trip bookings like flights and hotels for review, and surfaces post-trip charges with a one-tap exclude. Your Netflix charge never ends up in the group's bill.",
  },
  {
    q: 'Can I edit many transactions at once?',
    a: 'Yes — select any set of rows and bulk-assign people, change categories, confirm, or exclude in one tap. Bulk editing across a statement is an AutoSplit-only feature.',
  },
]

const STEPS = [
  {
    icon: FileText,
    title: 'Drop in the statement',
    body: 'PDF or CSV from Chase, Amex, Bank of America, and most others — or snap a screenshot of your banking app. Every charge is extracted in seconds.',
  },
  {
    icon: CalendarRange,
    title: 'Trip dates do the sorting',
    body: 'Everyday spending outside your dates is auto-excluded. Flights booked months early are flagged, not lost. Post-trip Ubers get a one-tap exclude.',
  },
  {
    icon: CheckSquare,
    title: 'Bulk-triage what’s left',
    body: 'Select 20 rows, hit "Set Participants" once. Assign, categorize, confirm, or exclude entire groups of charges in single taps.',
  },
]

export default function StatementSplitPage() {
  // FAQPage JSON-LD — what search engines and AI assistants parse into
  // citation-ready snippets (the GEO play).
  useEffect(() => {
    const id = 'statement-faq-jsonld'
    let el = document.getElementById(id)
    if (!el) {
      el = document.createElement('script')
      el.id = id
      el.type = 'application/ld+json'
      document.head.appendChild(el)
    }
    el.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question', name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    })
    return () => document.getElementById(id)?.remove()
  }, [])

  return (
    <PublicShell
      title="Split a Whole Bank Statement With Friends — AutoSplit"
      description="Upload a credit card statement (PDF, CSV, or a banking-app screenshot) and split every charge with your group. No bank linking, no typing expenses one by one."
    >
      <main className="relative z-10 max-w-5xl mx-auto px-6 pt-8 pb-16">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mb-4 leading-tight">
            Split the whole trip,<br />not one receipt at a time
          </h1>
          <p className="text-ink-300 text-lg leading-relaxed mb-7">
            Came home to 47 shared charges on your card? Don't type them into a
            splitting app one by one. Upload the statement — AutoSplit reads
            every transaction, sorts trip from not-trip, and works out who owes whom.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-lime-400 text-ink-950 font-bold hover:bg-lime-500 transition-colors shadow-lg shadow-lime-400/20"
          >
            Split my statement <ArrowRight size={16} />
          </Link>
          <p className="text-xs text-ink-500 mt-3">Free while in beta · no bank linking · no expense caps</p>
        </div>

        {/* ── Steps ────────────────────────────────────────────────────── */}
        <div className="grid md:grid-cols-3 gap-5 mb-14">
          {STEPS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-ink-700 bg-ink-900/70 p-6">
              <div className="w-10 h-10 rounded-xl bg-lime-400/10 border border-lime-400/25 flex items-center justify-center mb-4">
                <Icon size={18} className="text-lime-400" />
              </div>
              <h2 className="font-display text-lg font-semibold mb-2">{title}</h2>
              <p className="text-sm text-ink-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        {/* ── The privacy angle ────────────────────────────────────────── */}
        <div className="max-w-3xl mx-auto rounded-2xl border border-ink-700 bg-ink-900/50 p-7 mb-14 flex items-start gap-4">
          <ShieldCheck size={22} className="text-lime-400 flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-display text-lg font-semibold mb-1.5">Your bank stays yours</h2>
            <p className="text-sm text-ink-300 leading-relaxed">
              Other import features want your bank credentials. AutoSplit never
              connects to your bank — you share exactly one file, the statement
              you already downloaded. Nothing more to revoke, nothing to leak.
            </p>
          </div>
        </div>

        {/* ── Screenshot path callout ──────────────────────────────────── */}
        <div className="max-w-3xl mx-auto rounded-2xl border border-ink-700 bg-ink-900/50 p-7 mb-14 flex items-start gap-4">
          <Camera size={22} className="text-lime-400 flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-display text-lg font-semibold mb-1.5">No statement handy? Screenshot your banking app</h2>
            <p className="text-sm text-ink-300 leading-relaxed">
              Scroll your transactions, screenshot the list, upload the image.
              AutoSplit reads every visible row — same result as a statement,
              straight from your phone.
            </p>
          </div>
        </div>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-2xl font-semibold mb-6">Questions people ask</h2>
          <div className="space-y-5">
            {FAQ.map(({ q, a }) => (
              <div key={q}>
                <h3 className="text-sm font-semibold text-ink-100 mb-1">{q}</h3>
                <p className="text-sm text-ink-400 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </PublicShell>
  )
}
